
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as exposed from '../exposed'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * LoginMAParams struct
 */
class LoginMAParams {
  /**
   * Initialize struct
   * @param {!number=} tradingAccountId
   * @constructor
   */
  constructor (tradingAccountId = new UInt64(0, 0)) {
    this.TradingAccountId = tradingAccountId
  }

  /**
   * Copy struct (shallow copy)
   * @this {!LoginMAParams}
   * @param {!LoginMAParams} other Other struct
   * @returns {!LoginMAParams} This struct
   */
  copy (other) {
    if (other.TradingAccountId != null) {
      this.TradingAccountId = UInt64.fromNumber(other.TradingAccountId)
    } else {
      this.TradingAccountId = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!LoginMAParams}
   * @returns {!LoginMAParams} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new LoginMAParamsModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new LoginMAParamsModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!LoginMAParams}
   * @param {!LoginMAParams} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof LoginMAParams)) {
      throw new TypeError('Instance of LoginMAParams is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!LoginMAParams}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      TradingAccountId: ((this.TradingAccountId != null) ? this.TradingAccountId.toNumber() : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return LoginMAParams.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!LoginMAParams} other Object value
   * @returns {!LoginMAParams} Created struct
   */
  static fromObject (other) {
    return new LoginMAParams().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!LoginMAParams}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return LoginMAParams.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!LoginMAParams}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 5164
  }
}

export { LoginMAParams };

/**
 * Fast Binary Encoding LoginMAParams field model
 */
class FieldModelLoginMAParams extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.TradingAccountId = new fbe.FieldModelUInt64(buffer, 4 + 4)
  }

  /**
   * Get the field size
   * @this {!FieldModelLoginMAParams}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelLoginMAParams}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.TradingAccountId.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelLoginMAParams}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.TradingAccountId.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelLoginMAParams}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelLoginMAParams.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelLoginMAParams}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 5164
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelLoginMAParams}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelLoginMAParams}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.TradingAccountId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.TradingAccountId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.TradingAccountId.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelLoginMAParams}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelLoginMAParams}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelLoginMAParams}
   * @param {!LoginMAParams} fbeValue Default value, defaults is new LoginMAParams()
   * @returns {!LoginMAParams} LoginMAParams value
   */
  get (fbeValue = new LoginMAParams()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelLoginMAParams}
   * @param {!LoginMAParams} fbeValue LoginMAParams value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.TradingAccountId.fbeSize) <= fbeStructSize) {
      fbeValue.TradingAccountId = this.TradingAccountId.get()
    } else {
      fbeValue.TradingAccountId = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.TradingAccountId.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelLoginMAParams}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelLoginMAParams}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelLoginMAParams}
   * @param {!LoginMAParams} fbeValue LoginMAParams value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelLoginMAParams}
   * @param {LoginMAParams} fbeValue LoginMAParams value
   */
  setFields (fbeValue) {
    this.TradingAccountId.set(fbeValue.TradingAccountId)
  }
}

export { FieldModelLoginMAParams };

/**
 * Fast Binary Encoding LoginMAParams model
 */
class LoginMAParamsModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelLoginMAParams(this.buffer, 4)
  }

  /**
   * Get the LoginMAParams model
   * @this {!LoginMAParamsModel}
   * @returns {!FieldModelLoginMAParams} model LoginMAParams model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!LoginMAParamsModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!LoginMAParamsModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return LoginMAParamsModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!LoginMAParamsModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelLoginMAParams.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!LoginMAParamsModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!LoginMAParamsModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!LoginMAParamsModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!LoginMAParamsModel}
   * @param {!LoginMAParams} value LoginMAParams value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!LoginMAParamsModel}
   * @param {!LoginMAParams} value LoginMAParams value, defaults is new LoginMAParams()
   * @return {!object} Deserialized LoginMAParams value and its size
   */
  deserialize (value = new LoginMAParams()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new LoginMAParams(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new LoginMAParams(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!LoginMAParamsModel}
   * @param {!number} prev Previous LoginMAParams model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { LoginMAParamsModel };
